import React, { useEffect, useState } from "react";
import CustomButton from "../../Core/CommonV2/CustomButton";
import { Checkbox, Popover, DatePicker } from "antd";
import { useAppDispatch, useAppSelector } from "../../Core/redux/hooks";
import CustomAvatar from "../../Core/CommonV2/CustomAvatar";
import getUsersName from "../../Core/utils/getUserName";
import { ChevronIcon } from "../../Core/svgV2/Chevron";
import { updateVault } from "../../Core/redux/slices/vault.slice";
import { PhaseIcon } from "../../Core/svgV2/PhaseIcon";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const FilterLayout = ({
  filterName,
  children,
  footer = false,
  onClear = () => null,
  onClose = () => null,
}) => {
  const [filterOpen, setFilterOpen] = useState(true);

  const handleClose = () => {
    setFilterOpen(false);
    onClose();
  };

  useEffect(() => {
    setFilterOpen(true);
  }, [filterName]);

  return (
    <div
      className="w-[320px] bg-white border border-gray-100 rounded-lg"
      style={{
        boxShadow:
          "0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)",
      }}
    >
      <h3 className="out-300-14 capitalize pt-[14px] pb-2.5 px-[14px] border-b border-gray-200 text-gray-500">
        {filterName}
      </h3>
      <div className="max-h-[304px] overflow-y-auto">{children}</div>

      {footer && (
        <div className="flex border-t border-gray-200 pt-[2px] pb-[2px] justify-end items-center">
          <CustomButton
            className="bg-transparent out-500-14 text-gray-500"
            text="Clear"
            onClick={onClear}
          />
          <CustomButton
            className="bg-transparent out-500-14 text-primary-600"
            text="Done"
            onClick={handleClose}
          />
        </div>
      )}
    </div>
  );
};

const TypeFilter = ({ onClose = () => null }) => {
  const dispatch = useAppDispatch();
  const { filters } = useAppSelector((state) => state.vault);
  const typeFilters = filters?.type || [];

  const handleClick = (type) => {
    let newTypeFilters;
    if (typeFilters.includes(type)) {
      newTypeFilters = typeFilters.filter((t) => t !== type);
    } else {
      newTypeFilters = [...typeFilters, type];
    }

    dispatch(
      updateVault({
        key: "filters",
        value: { ...filters, type: newTypeFilters },
      })
    );
  };

  const types = [
    {
      id: "folder",
      name: "Folder",
    },
    { id: "file", name: "File" },
    { id: "link", name: "Link" },
  ];

  return (
    <FilterLayout
      filterName="Type"
      footer={true}
      onClose={onClose}
      onClear={() =>
        dispatch(
          updateVault({
            key: "filters",
            value: { ...filters, type: [] },
          })
        )
      }
    >
      {types.map((type) => (
        <div
          key={type.id}
          role="button"
          onClick={() => handleClick(type.id)}
          className="flex cursor-pointer hover:bg-gray-50 justify-between px-[14px] py-2.5"
        >
          <div className="flex items-center gap-x-2">
            <span className="out-300-14 text-gray-900">{type.name}</span>
          </div>
          <Checkbox checked={typeFilters.includes(type.id)} />
        </div>
      ))}
    </FilterLayout>
  );
};

const OwnerFilter = ({ onClose = () => null }) => {
  const dispatch = useAppDispatch();
  const { filters } = useAppSelector((state) => state.vault);
  const { tenantMembers } = useAppSelector((state) => state.tenant);
  const ownerFilters = filters?.owner || [];

  const handleClick = (userId) => {
    let newOwnerFilters;
    if (ownerFilters.includes(userId)) {
      newOwnerFilters = ownerFilters.filter((id) => id !== userId);
    } else {
      newOwnerFilters = [...ownerFilters, userId];
    }

    dispatch(
      updateVault({
        key: "filters",
        value: { ...filters, owner: newOwnerFilters },
      })
    );
  };

  return (
    <FilterLayout
      filterName="Owner"
      footer={true}
      onClose={onClose}
      onClear={() =>
        dispatch(
          updateVault({
            key: "filters",
            value: { ...filters, owner: [] },
          })
        )
      }
    >
      {tenantMembers?.map((user) => (
        <div
          key={user.userId}
          role="button"
          onClick={() => handleClick(user.userId)}
          className="flex cursor-pointer hover:bg-gray-50 justify-between px-[14px] py-2.5"
        >
          <div className="flex items-center gap-x-2">
            <CustomAvatar
              src={user.profilePicture}
              title={getUsersName(user)}
              size={24}
              whiteText
              color={user?.profilePictureColorCode}
              fontSize="12px"
            />
            <span className="out-300-14 text-gray-900">
              {getUsersName(user)}
            </span>
          </div>
          <Checkbox checked={ownerFilters.includes(user.userId)} />
        </div>
      ))}
    </FilterLayout>
  );
};

const PhaseFilter = ({ onClose = () => null }) => {
  const dispatch = useAppDispatch();
  const { filters } = useAppSelector((state) => state.vault);
  const { phases } = useAppSelector((state) => state.schedule);
  const phaseFilters = filters?.phase || [];

  const handleClick = (phaseId) => {
    let newPhaseFilters;
    if (phaseFilters.includes(phaseId)) {
      newPhaseFilters = phaseFilters.filter((id) => id !== phaseId);
    } else {
      newPhaseFilters = [...phaseFilters, phaseId];
    }

    dispatch(
      updateVault({
        key: "filters",
        value: { ...filters, phase: newPhaseFilters },
      })
    );
  };

  return (
    <FilterLayout
      filterName="Phase"
      footer={true}
      onClose={onClose}
      onClear={() =>
        dispatch(
          updateVault({
            key: "filters",
            value: { ...filters, phase: [] },
          })
        )
      }
    >
      {phases?.map((p) => (
        <div
          key={p.id}
          role="button"
          onClick={() => handleClick(p.projectPhaseId)}
          className="flex cursor-pointer hover:bg-gray-50 justify-between px-[14px] py-2.5"
        >
          <div className="flex items-center gap-x-2">
            <PhaseIcon height="16px" width="16px" phaseId={+p.phaseId} />
            <p className="out-300-14 text-gray-900">
              {p.phases} {p.phaseIteration}
            </p>
          </div>
          <Checkbox checked={phaseFilters.includes(p.projectPhaseId)} />
        </div>
      ))}
    </FilterLayout>
  );
};

const DateUploadedFilter = ({ onClose = () => null }) => {
  const dispatch = useAppDispatch();
  const { filters } = useAppSelector((state) => state.vault);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(
    filters?.dateUploaded
      ? [
          dayjs(filters.dateUploaded.startDate),
          dayjs(filters.dateUploaded.endDate),
        ]
      : null
  );

  const handleDateChange = (dates: [dayjs.Dayjs, dayjs.Dayjs] | null) => {
    setDateRange(dates);
    if (dates && dates.length === 2) {
      dispatch(
        updateVault({
          key: "filters",
          value: {
            ...filters,
            dateUploaded: {
              startDate: dates[0].format("YYYY-MM-DD"),
              endDate: dates[1].format("YYYY-MM-DD"),
            },
          },
        })
      );
    } else {
      dispatch(
        updateVault({
          key: "filters",
          value: { ...filters, dateUploaded: null },
        })
      );
    }
  };

  const clearDateRange = () => {
    setDateRange(null);
    dispatch(
      updateVault({
        key: "filters",
        value: { ...filters, dateUploaded: null },
      })
    );
  };

  return (
    <FilterLayout
      filterName="Date Uploaded"
      footer={true}
      onClose={onClose}
      onClear={clearDateRange}
    >
      <div className="px-[14px] py-2.5">
        <RangePicker
          value={dateRange}
          onChange={handleDateChange}
          placeholder={["Start Date", "End Date"]}
          className="w-full"
          format="YYYY-MM-DD"
        />
      </div>
    </FilterLayout>
  );
};

const FilterItem = ({ filterName, filterContent }) => {
  const [filterOpen, setFilterOpen] = useState(false);
  return (
    <Popover
      getPopupContainer={(parent) => parent?.parentElement}
      trigger="click"
      content={filterContent}
      arrow={false}
      placement="rightBottom"
      onOpenChange={setFilterOpen}
      open={filterOpen}
    >
      <div
        className="cursor-pointer hover:bg-gray-50 px-3.5 py-2.5"
        onClick={() => setFilterOpen(true)}
      >
        <p className="text-gray-900 out-300-14">{filterName}</p>
      </div>
    </Popover>
  );
};

const LibraryFilters = ({ onClose = () => null }) => {
  return (
    <div>
      <FilterLayout filterName="Filters" onClose={onClose}>
        <FilterItem
          filterContent={<TypeFilter onClose={onClose} />}
          filterName="Type"
        />
        <FilterItem
          filterContent={<OwnerFilter onClose={onClose} />}
          filterName="Owner"
        />
        <FilterItem
          filterContent={<PhaseFilter onClose={onClose} />}
          filterName="Phase"
        />
        <FilterItem
          filterContent={<DateUploadedFilter onClose={onClose} />}
          filterName="Date Uploaded"
        />
      </FilterLayout>
    </div>
  );
};

const AppliedFilterItem = ({ filterName, filterCount, filterContent }) => {
  return (
    <Popover
      trigger="click"
      content={filterContent}
      arrow={false}
      placement="bottomLeft"
    >
      <button className="flex items-center justify-center px-2.5 py-[4px] gap-x-2 rounded border border-gray-200 bg-white">
        <p className="out-500-14 text-gray-700">
          {filterName} : {filterCount}
        </p>
        <ChevronIcon className="text-gray-700" />
      </button>
    </Popover>
  );
};

export const AppliedLibraryFilters = ({ style = {} }) => {
  const dispatch = useAppDispatch();
  const { filters } = useAppSelector((state) => state.vault);

  const typeFilters = filters?.type || [];
  const ownerFilters = filters?.owner || [];
  const phaseFilters = filters?.phase || [];
  const dateUploadedFilter = filters?.dateUploaded;

  const allFilters = [
    ...typeFilters,
    ...ownerFilters,
    ...phaseFilters,
    ...(dateUploadedFilter ? [dateUploadedFilter] : []),
  ];

  const resetAllFilters = () => {
    dispatch(
      updateVault({
        key: "filters",
        value: {},
      })
    );
  };

  return (
    <div className="flex items-center gap-x-2 flex-wrap" style={style}>
      {allFilters.length > 0 && (
        <CustomButton
          text="Reset All filters"
          height="30px"
          className="bg-transparent text-gray-500 out-500-14"
          onClick={resetAllFilters}
        />
      )}
      {typeFilters.length > 0 && (
        <AppliedFilterItem
          filterCount={typeFilters.length}
          filterName="Type"
          filterContent={<TypeFilter />}
        />
      )}
      {ownerFilters.length > 0 && (
        <AppliedFilterItem
          filterCount={ownerFilters.length}
          filterName="Owner"
          filterContent={<OwnerFilter />}
        />
      )}
      {phaseFilters.length > 0 && (
        <AppliedFilterItem
          filterCount={phaseFilters.length}
          filterName="Phase"
          filterContent={<PhaseFilter />}
        />
      )}
      {dateUploadedFilter && (
        <AppliedFilterItem
          filterCount={1}
          filterName="Date Uploaded"
          filterContent={<DateUploadedFilter />}
        />
      )}
    </div>
  );
};

export default LibraryFilters;
